import { useLocale } from "@hooks/useLocale";
import { useCurrentWell } from "@hooks/useCurrentWell";
import { QueryState } from "@components/ui/QueryState";
import { StatBand, type Stat } from "@components/ui/StatBand";
import { useGetEventSafetyQuery } from "@api/pApi";
import { HseFullJob } from "./HseFullJob";
import { fmtNum } from "@lib/format";
import type { SafetyOut } from "@api/types";
import type { Locale } from "@i18n/locales";

/** Locale-aware date for a HSE trail row (date_report is a plain YYYY-MM-DD). */
function fmtDate(iso: string | null, locale: Locale): string {
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(locale, { year: "numeric", month: "short", day: "numeric" });
}

/** A pressure served in psi; null renders the em-dash. */
function fmtPressure(v: number | null, locale: Locale, psi: string): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return `${fmtNum(v, locale, { maximumFractionDigits: 0 })} ${psi}`;
}

/**
 * HSE / safety — the daily HSE header trail and LTI counters for the current
 * well's active event, served by Oxbo-P (/events/{id}/safety). Every number is
 * server-supplied; the FE only formats and lays out. dm_safety is a per-day
 * header (not a per-observation incident row), so this surfaces the day's HSE
 * state and the live LTI counters — it does NOT synthesise incident counts.
 */
export function Hse() {
  const { t } = useLocale();
  const { well, activeEvent, isLoading, isError } = useCurrentWell();
  return (
    <div>
      <div className="page-head">
        <h1>{t("hse.title")}</h1>
        <p className="subtitle">
          {well ? `${well.commonName} · ${well.field}` : t("well.notSelected")}
        </p>
      </div>
      <QueryState
        isLoading={isLoading}
        isError={isError}
        isEmpty={!activeEvent}
        emptyMessage={t("well.event.noEvents")}
      >
        {well?.uwi ? <HseFullJob wellUwi={well.uwi} /> : null}
        {activeEvent ? <SafetyPanel eventId={activeEvent.id} /> : null}
      </QueryState>
    </div>
  );
}

/** The HSE panel — also embeddable in a DDR HSE tab. */
export function SafetyPanel({ eventId }: { eventId: string }) {
  const { t, locale } = useLocale();
  const { data, isLoading, isError, refetch } = useGetEventSafetyQuery(eventId, {
    skip: !eventId,
  });

  const items = data?.items ?? [];
  // The most-recent HSE header (items are served most-recent first) carries the
  // day's well-control / compliance / inspection state for the summary cards.
  const latest: SafetyOut | undefined = items[0];

  const yesNo = (v: boolean | null): string =>
    v == null ? "—" : v ? t("hse.inspections.yes") : t("hse.inspections.no");

  const stats: Stat[] = data
    ? [
        {
          label: t("hse.kpi.ltiOperator"),
          value: fmtNum(data.days_since_lti_operator, locale),
          tone: "good",
        },
        {
          label: t("hse.kpi.ltiTotal"),
          value: fmtNum(data.days_since_lti_total, locale),
          tone: "good",
        },
        { label: t("hse.kpi.reports"), value: fmtNum(items.length, locale) },
      ]
    : [];

  return (
    <QueryState
      isLoading={isLoading}
      isError={isError}
      isEmpty={!!data && items.length === 0}
      emptyMessage={t("hse.noData")}
      onRetry={refetch}
    >
      <div className="page-head" style={{ marginBottom: 12 }}>
        <p className="subtitle">{t("hse.subtitle")}</p>
      </div>
      {stats.length ? <StatBand stats={stats} /> : null}

      <div className="grid-2" style={{ marginTop: 14 }}>
        <div className="card">
          <h3>{t("hse.wellControl")}</h3>
          <table>
            <tbody>
              <tr data-owner="p">
                <td>{t("hse.wellControl.bopAnnular")}</td>
                <td className="num tab-nums">
                  {fmtPressure(latest?.bop_pressure_annular ?? null, locale, t("hse.unit.psi"))}
                </td>
              </tr>
              <tr data-owner="p">
                <td>{t("hse.wellControl.lastCasingTest")}</td>
                <td className="num tab-nums">
                  {fmtPressure(
                    latest?.last_casing_test_pressure ?? null,
                    locale,
                    t("hse.unit.psi"),
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="card">
          <h3>{t("hse.compliance")}</h3>
          <table>
            <tbody>
              <tr data-owner="p">
                <td>{t("hse.compliance.ridCards")}</td>
                <td className="num tab-nums">{fmtNum(latest?.rid_cards ?? null, locale)}</td>
              </tr>
              <tr data-owner="p">
                <td>{t("hse.compliance.jsa")}</td>
                <td className="num tab-nums">
                  {fmtNum(latest?.job_safety_analysis ?? null, locale)}
                </td>
              </tr>
              <tr data-owner="p">
                <td>{t("hse.compliance.jobsiteEval")}</td>
                <td className="num tab-nums">
                  {fmtNum(latest?.jobsite_evaluation ?? null, locale)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="card">
          <h3>{t("hse.inspections")}</h3>
          <table>
            <tbody>
              <tr data-owner="p">
                <td>{t("hse.inspections.company")}</td>
                <td>
                  <span
                    className={`badge ${
                      latest?.is_company_inspected ? "status-drilling" : "status-completion"
                    }`}
                  >
                    {yesNo(latest?.is_company_inspected ?? null)}
                  </span>
                </td>
              </tr>
              <tr data-owner="p">
                <td>{t("hse.inspections.government")}</td>
                <td>
                  <span
                    className={`badge ${
                      latest?.is_government_inspected ? "status-drilling" : "status-completion"
                    }`}
                  >
                    {yesNo(latest?.is_government_inspected ?? null)}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="card" style={{ marginTop: 14 }}>
        <h3>{t("hse.trail")}</h3>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>{t("hse.trail.date")}</th>
                <th className="num">{t("hse.trail.ltiOperator")}</th>
                <th className="num">{t("hse.trail.ltiTotal")}</th>
                <th className="num">{t("hse.trail.ridCards")}</th>
                <th className="num">{t("hse.trail.jsa")}</th>
                <th className="num">{t("hse.trail.jobsiteEval")}</th>
                <th className="num">{t("hse.trail.bopAnnular")}</th>
                <th className="num">{t("hse.trail.casingTest")}</th>
                <th>{t("hse.trail.company")}</th>
                <th>{t("hse.trail.government")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.id} data-owner="p">
                  <td style={{ whiteSpace: "nowrap" }}>{fmtDate(s.date_report, locale)}</td>
                  <td className="num tab-nums">{fmtNum(s.days_since_lti_operator, locale)}</td>
                  <td className="num tab-nums">{fmtNum(s.days_since_lti_total, locale)}</td>
                  <td className="num tab-nums">{fmtNum(s.rid_cards, locale)}</td>
                  <td className="num tab-nums">{fmtNum(s.job_safety_analysis, locale)}</td>
                  <td className="num tab-nums">{fmtNum(s.jobsite_evaluation, locale)}</td>
                  <td className="num tab-nums">
                    {fmtPressure(s.bop_pressure_annular, locale, t("hse.unit.psi"))}
                  </td>
                  <td className="num tab-nums">
                    {fmtPressure(s.last_casing_test_pressure, locale, t("hse.unit.psi"))}
                  </td>
                  <td>{yesNo(s.is_company_inspected)}</td>
                  <td>{yesNo(s.is_government_inspected)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </QueryState>
  );
}
